// An Audiotool project as Live tracks: AudiotoolProjection → PullPlan.
//
// The inverse of push-plan.ts, built by inverting what the SDK's own writer
// does (spike IS0–IS7): region-local time t plays collection time
//     collectionOffset + ((t − loopOffset) mod loopDuration)
// and an audio region's collection time c plays its sample at fraction
// c / musicTicks. Everything below works in ticks and sample FRACTIONS, so no
// step depends on either project's tempo:
//
//   • Positions are musical. A region at bar 5 lands on Live beat 16 whether
//     Audiotool runs at 174 and Live at 120 or the reverse — beats = ticks /
//     3840, nothing is scaled by a tempo. (Any mapper that goes through
//     ticksToSeconds is wrong; the spec makes that its first case.)
//   • Tempo matters only for whether a slice of audio FITS its beats in Live.
//     A pulled clip is an unwarped file cut from the sample, as long as the
//     file; at the Set's final tempo it spans durationSeconds·tempo/60 beats of
//     the sample. When that disagrees with musicTicks by more than 1%, the clip
//     is marked `stretched` and the extension warps it.
//
// Audio: each loop repetition of a region becomes its own Live clip — a Live
// receiver can set `looping` but not a loop RANGE (the reason flattenMidiClip
// exists for MIDI). Collection time outside [0, musicTicks) is silence, so a
// region longer than its sample stops where the sample does.
//
// MIDI: a note region becomes ONE Live MIDI clip spanning the region, its
// repetitions unrolled into the note list and each note cut at the end of its
// repetition — the flattened, clip-relative form import.ts's MidiClipPlan
// already takes.
//
// Mixer: Live's fader is set from device gain × mixer-strip gain (in series
// in Audiotool, one fader in Live); pan, mute and solo from the strip.
//
// What doesn't come across, named in a warning rather than dropped silently:
// every device (a note track arrives as an empty MIDI lane with the right
// notes — Live's SDK cannot insert an instrument on a MIDI track), automation,
// tempo automation (its own warning: the beat↔time map is no longer uniform),
// a non-4/4 signature, note slide, and hand-drawn playback-speed curves.
//
// Pure: no I/O, no SDK. Tested from workers/test/audiotool-pull-plan.spec.ts.

import type { WireNote } from "../midi/notes.js";
import { CENTER_PAN, UNITY_VOLUME, clampPan, dbToFader } from "../mixer.js";
import type { AudiotoolProjection, ProjAudioRegion, ProjNoteRegion, ProjRegionTiming, ProjSample } from "./projection.js";
import { TICKS_PER_BEAT, ticksToBeats, unitToVelocity } from "./ticks.js";

export interface PullOptions {
  /** The Set's tempo now. */
  liveTempo: number;
  /** The import path's own TempoChoice, minus "same" (which is just "keep"). */
  tempoChoice: "match" | "keep";
}

/** Structurally import.ts's TrackSettings. */
export interface PullTrackSettings {
  mute: boolean;
  solo: boolean;
  arm: boolean;
  volume: number;
  panning: number;
}

export interface PullAudioClip {
  sampleName: string;
  /** Beats. */
  startTime: number;
  /** Beats. */
  duration: number;
  /** The slice of the sample this clip plays, as fractions of its length. */
  fromFraction: number;
  toFraction: number;
  name: string;
  muted: boolean;
  /** The slice will not fill its beats unwarped at the Set's tempo. */
  stretched: boolean;
  /**
   * Audiotool plays this clip as something other than its sample, verbatim:
   * stretched, a drawn speed curve, region gain, a fade, or a pitch shift. A
   * cut of the sample is then not what the producer hears.
   */
  altered: boolean;
}

export interface PullAudioTrack {
  /** The Audiotool track entity. */
  id: string;
  /** Its mixer channel, when cabled to one. */
  stripId: string | null;
  /** Effects, channel processing or automation change its sound on the way to the mixer. */
  processed: boolean;
  name: string;
  settings: PullTrackSettings;
  clips: PullAudioClip[];
}

/** Structurally import.ts's MidiClipPlan. */
export interface PullMidiClip {
  startTime: number;
  duration: number;
  notes: WireNote[];
  name: string;
  color: number | null;
  muted: boolean;
}

/** Structurally import.ts's MidiTrackPlan. */
export interface PullMidiTrack {
  /** The Audiotool track entity. */
  id: string;
  /** Its mixer channel, when its instrument is cabled to one. */
  stripId: string | null;
  name: string;
  settings: PullTrackSettings;
  clips: PullMidiClip[];
}

export type PullWarningCode =
  | "no-config"
  | "tempo-automation"
  | "signature-dropped"
  | "device-dropped"
  | "automation-dropped"
  | "slide-dropped"
  | "stretched"
  | "playback-curve";

export interface PullWarning {
  code: PullWarningCode;
  /** User-facing. */
  detail: string;
  count: number;
}

export interface PullPlan {
  /** What the Set's tempo should be after the pull. */
  tempo: number;
  /** The tempo the Audiotool project runs at. */
  sourceTempo: number;
  audioTracks: PullAudioTrack[];
  midiTracks: PullMidiTrack[];
  /** Distinct samples to download, in first-use order. */
  samples: ProjSample[];
  warnings: PullWarning[];
}

/** Beyond this, an unwarped slice audibly drifts from its beats. */
const STRETCH_TOLERANCE = 0.01;

export function planPull(p: AudiotoolProjection, opts: PullOptions): PullPlan {
  const warnings: PullWarning[] = [];
  const warn = (code: PullWarningCode, detail: string, count = 1): void => {
    const w = warnings.find((x) => x.code === code);
    if (w) w.count += count;
    else warnings.push({ code, detail, count });
  };

  const sourceTempo = p.config?.tempoBpm ?? opts.liveTempo;
  const tempo = opts.tempoChoice === "match" && p.config ? p.config.tempoBpm : opts.liveTempo;
  if (!p.config) warn("no-config", "The Audiotool project has no tempo set yet, so the Set's tempo was kept.");
  if (p.tempoAutomation) {
    warn("tempo-automation",
      "The Audiotool project has tempo automation; clips land on the right beats but tempo changes are not copied.");
  }
  if (p.config && (p.config.signatureNumerator !== 4 || p.config.signatureDenominator !== 4)) {
    warn("signature-dropped",
      `The project is in ${p.config.signatureNumerator}/${p.config.signatureDenominator}; ` +
        "Live's time signature can't be set from here, so bar lines won't match.");
  }

  const devices = p.unmapped.filter((u) => u.kind === "device");
  if (devices.length > 0) {
    const list = devices.map((d) => `${label(d.entityType)}${d.count > 1 ? ` ×${d.count}` : ""}`).join(", ");
    warn("device-dropped", `Instruments and effects stay in Audiotool: ${list}. MIDI tracks arrive without an instrument.`,
      devices.reduce((n, d) => n + d.count, 0));
  }
  const automation = p.unmapped.filter((u) => u.kind === "automation").reduce((n, u) => n + u.count, 0);
  if (automation > 0) warn("automation-dropped", "Automation isn't copied.", automation);

  const samples: ProjSample[] = [];
  const seenSamples = new Set<string>();
  let unnamed = 0;
  const nameOf = (displayName: string): string => (displayName.trim() !== "" ? displayName : `Audiotool ${++unnamed}`);

  const audioTracks: PullAudioTrack[] = [...p.audioTracks]
    .sort((a, b) => a.orderAmongTracks - b.orderAmongTracks)
    .map((t) => {
      const clips: PullAudioClip[] = [];
      for (const r of [...t.regions].sort((a, b) => a.positionTicks - b.positionTicks)) {
        if (!r.playbackLinear) {
          warn("playback-curve", "Some Audiotool clips have a drawn playback-speed curve; they were pulled at a constant speed.");
        }
        const pulled = audioClips(r, tempo);
        if (pulled.length > 0 && !seenSamples.has(r.sample.name)) {
          seenSamples.add(r.sample.name);
          samples.push(r.sample);
        }
        for (const c of pulled) {
          if (c.stretched) {
            warn("stretched", "Some clips were recorded at a different tempo than they play at in Audiotool; check their warp markers in Live.");
          }
          clips.push(c);
        }
      }
      return {
        id: t.id,
        stripId: t.strip?.id ?? null,
        processed: t.processed ?? false,
        name: nameOf(t.displayName),
        settings: {
          mute: !t.enabled || (t.strip?.muted ?? false),
          solo: t.strip?.soloed ?? false,
          arm: false,
          // Device gain, channel input gain and fader are in series; Live has
          // one fader for all three.
          volume: dbToFader(toDb(t.gain) + toDb(t.strip?.preGain ?? 1) + toDb(t.strip?.gain ?? 1)),
          panning: clampPan(t.strip ? t.strip.panning : t.panning),
        },
        clips,
      };
    });

  const midiTracks: PullMidiTrack[] = [...p.noteTracks]
    .sort((a, b) => a.orderAmongTracks - b.orderAmongTracks)
    .map((t) => ({
      id: t.id,
      stripId: t.strip?.id ?? null,
      name: nameOf(t.displayName),
      // The instrument's own gain doesn't come across (the instrument doesn't),
      // so only the strip's level applies.
      settings: {
        mute: !t.enabled || (t.strip?.muted ?? false),
        solo: t.strip?.soloed ?? false,
        arm: false,
        volume: t.strip ? dbToFader(toDb(t.strip.preGain ?? 1) + toDb(t.strip.gain)) : UNITY_VOLUME,
        panning: t.strip ? clampPan(t.strip.panning) : CENTER_PAN,
      },
      clips: [...t.regions]
        .sort((a, b) => a.positionTicks - b.positionTicks)
        .map((r) => midiClip(r, () => warn("slide-dropped", "Live notes can't slide; slides were dropped."))),
    }));

  return { tempo, sourceTempo, audioTracks, midiTracks, samples, warnings };
}

/** A stretch of a region's time and the collection time it plays. */
interface Segment {
  /** Region-local start, ticks. */
  at: number;
  /** Collection time played, [from, to). */
  from: number;
  to: number;
}

/**
 * Unroll a region's loop into segments of collection time.
 *
 * Before loopOffset the region plays the collection leading up to
 * collectionOffset (the SDK never writes loopOffset ≠ 0, but a human can);
 * from there, one segment per repetition of loopDuration, the last cut at the
 * region's end. A non-positive loopDuration means no loop.
 */
function unroll(r: ProjRegionTiming): Segment[] {
  const d = r.durationTicks;
  const lo = Math.max(0, Math.min(r.loopOffsetTicks, d));
  const c = r.collectionOffsetTicks;
  const out: Segment[] = [];
  if (lo > 0) out.push({ at: 0, from: c - lo, to: c });
  const len = r.loopDurationTicks > 0 ? r.loopDurationTicks : d - lo;
  for (let at = lo; at < d; at += len) {
    const span = Math.min(len, d - at);
    out.push({ at, from: c, to: c + span });
  }
  return out;
}

function audioClips(r: ProjAudioRegion, tempo: number): PullAudioClip[] {
  const m = r.musicTicks;
  if (!(m > 0)) return [];
  // The file's natural length in beats at the Set's final tempo, vs. the
  // musical length the region stretches it to.
  const naturalTicks = r.sample.durationSeconds * (tempo / 60) * TICKS_PER_BEAT;
  const stretched = Math.abs(naturalTicks - m) / m > STRETCH_TOLERANCE;

  const out: PullAudioClip[] = [];
  for (const s of unroll(r)) {
    // Clip the segment to where the sample actually has audio.
    const from = Math.max(s.from, 0);
    const to = Math.min(s.to, m);
    if (to <= from) continue;
    const at = s.at + (from - s.from);
    out.push({
      sampleName: r.sample.name,
      startTime: ticksToBeats(r.positionTicks + at),
      duration: ticksToBeats(to - from),
      fromFraction: from / m,
      toFraction: to / m,
      name: r.displayName,
      muted: !r.enabled,
      stretched,
      altered: stretched || !r.playbackLinear || Math.abs((r.gain ?? 1) - 1) > 1e-4 || (r.fadeInTicks ?? 0) > 0 || (r.fadeOutTicks ?? 0) > 0 || (r.pitchShiftSemitones ?? 0) !== 0,
    });
  }
  return out;
}

function midiClip(r: ProjNoteRegion, onSlide: () => void): PullMidiClip {
  const notes: WireNote[] = [];
  let slid = false;
  for (const s of unroll(r)) {
    for (const n of r.notes) {
      if (n.positionTicks < s.from || n.positionTicks >= s.to) continue;
      if (n.doesSlide) slid = true;
      const start = s.at + (n.positionTicks - s.from);
      const end = Math.min(start + n.durationTicks, s.at + (s.to - s.from));
      notes.push({
        pitch: n.pitch,
        startTime: ticksToBeats(start),
        duration: ticksToBeats(end - start),
        velocity: unitToVelocity(n.velocity),
      });
    }
  }
  if (slid) onSlide();
  notes.sort((a, b) => a.startTime - b.startTime || a.pitch - b.pitch);
  return {
    startTime: ticksToBeats(r.positionTicks),
    duration: ticksToBeats(r.durationTicks),
    notes,
    name: r.displayName,
    color: null,
    muted: !r.enabled,
  };
}

function toDb(linear: number): number {
  return linear > 0 ? 20 * Math.log10(linear) : Number.NEGATIVE_INFINITY;
}

/** "stompboxDelay" → "Stompbox Delay"; "heisenberg" → "Heisenberg". */
function label(entityType: string): string {
  const spaced = entityType.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
